import { useMemo } from 'react'
import {
  repVolume, CHART_NOTE, CHART_SOURCE_NOTE, CHART_EMPTY_NOTE, CHART_ERROR_NOTE,
  UNRESOLVABLE_NOTE,
} from '../../lib/bowlCoverage.js'
import styles from './repVolume.module.css'

/**
 * VOLUME PER REP — "who's carrying the bowl book and where are the holes."
 *
 * Brian: "a chart at the top that shows volume per rip [rep]."
 *
 * ⭐ A HORIZONTAL BAR LIST, NOT A PIE, AND THAT IS A PHONE DECISION. At 390px a
 * pie is a circle of unlabelled wedges with a legend underneath that you have to
 * look back and forth to use; the slice for a rep with 14 teams and the slice
 * for a rep with 17 are indistinguishable. A bar list puts the name, the number
 * and the length on one line, left-aligned, so it is read top-to-bottom like
 * any other list and the number never needs a tap.
 *
 * 🔴 THE NUMBERS ARE ON THE BARS, NOT IN A TOOLTIP. Brian: "the numbers should
 * be readable without tapping." A tooltip on a phone is a long-press nobody
 * discovers, so a charted value hidden in one is a value that is not published.
 *
 * 🔴 UNASSIGNED IS A BAR, AND SO IS NOT-READABLE, AND THEY ARE TWO BARS.
 * Brian: "If a slice of the market has no owner, that's the most important
 * thing on the chart, not an omission." Measured live 2026-10-17: 2 teams have
 * nobody covering their body (Notre Dame and UConn, FBS Independents, which has
 * no row in the breakdown) and 88 teams sit in conferences this board cannot
 * read an owner for at all. Those are different problems — one is a hole in the
 * assignment sheet, the other is a missing field on the payload — and one
 * combined "unassigned: 90" bar would send somebody to fix the wrong one.
 *
 * ⚠️ NEVER CAPPED AT THE FIRST N OF n. Every rep carrying a team gets a row.
 *
 * ⚠️ IT COMPUTES NO MONEY AND NO CREDIT. Teams, and only teams. Revenue and
 * attribution are manager-only surfaces and this is a coverage view.
 *
 * It reads; it never writes.
 */
export default function RepVolumeChart({
  status = 'ready',
  teams = null,
  membership = null,
  coverageFor = null,
  coverageStatus = 'ready',
  assignmentReadiness = 'unavailable',
  error = '',
  weekKey = '',
}) {
  const v = useMemo(
    () => (Array.isArray(teams) ? repVolume(teams, { coverageFor, membership }) : null),
    [teams, coverageFor, membership],
  )

  /* ---- ERROR ---------------------------------------------------------
   * 🔴 A FAILED READ MUST NEVER RENDER AS "NO RESULTS". Brian: that "would
   * read as 'nobody owns anything.'" So the error state says the board could
   * not be read, says it in the place the chart would have been, and prints
   * no bars at all — a zero-height bar chart is a claim. */
  if (status === 'error') {
    return (
      <section className={styles.wrap} aria-label="Volume per rep">
        <p className="eyebrow">Bowl coverage</p>
        <h2 className={styles.h}>Volume per rep</h2>
        <p className={styles.bad}>{CHART_ERROR_NOTE}</p>
        {error && <p className={styles.muted}>{error}</p>}
      </section>
    )
  }

  /* ---- LOADING — says what it is reading, and reserves the shape so the
   * page does not jump when it lands. */
  if (status === 'loading' || !v) {
    return (
      <section className={styles.wrap} aria-label="Volume per rep" aria-busy="true">
        <p className="eyebrow">Bowl coverage</p>
        <h2 className={styles.h}>Volume per rep</h2>
        <p className={styles.muted}>Counting all teams on the board by coverage rep…</p>
        <div className={styles.skeleton} aria-hidden="true">
          <i /><i /><i /><i />
        </div>
      </section>
    )
  }

  /* ---- EMPTY — a real answer, labelled as one. */
  if (v.total === 0) {
    return (
      <section className={styles.wrap} aria-label="Volume per rep">
        <p className="eyebrow">Bowl coverage</p>
        <h2 className={styles.h}>Volume per rep</h2>
        <p className={styles.muted}>{CHART_EMPTY_NOTE}</p>
      </section>
    )
  }

  const pct = (n) => `${Math.max(1.5, (n / v.max) * 100)}%`
  /* ⭐ ONE CLEAR ANSWER AT THE TOP, and on this chart the answer is the hole
   * rather than the leader: a rep scanning this wants to know what is NOT
   * covered. If everything is readable the answer flips to who carries most. */
  const holes = v.needsPick + v.noDesk + v.unresolvable
  const lead = v.bars[0]
  const barSum = v.bars.reduce((n, b) => n + b.n, 0)

  return (
    <section className={styles.wrap} aria-label="Volume per rep">
      <p className="eyebrow">Bowl coverage{weekKey ? ` · week ending ${weekKey}` : ''}</p>
      <h2 className={styles.h}>Volume per rep</h2>

      <p className={styles.answer}>
        {holes > 0
          ? (
            <>
              <b className={styles.answerNum}>{holes.toLocaleString()}</b>
              <span className={styles.answerOf}>
                of {v.total.toLocaleString()} teams on the board need a desk pick or have no readable desk
              </span>
            </>
          )
          : (
            <>
              <b className={styles.answerNum}>{lead ? lead.n.toLocaleString() : 0}</b>
              <span className={styles.answerOf}>
                teams covered by {lead ? lead.display : 'nobody'} — the most of any rep
              </span>
            </>
          )}
      </p>

      <p className={styles.muted}>
        Coverage across all teams on this board, not just teams currently bowl-eligible.
        People’s bars may overlap for confirmed school teams and provisional conference coverage; they are not counts of accountable assignments. Each school counts once in the company total.
      </p>
      {assignmentReadiness !== 'ready' && (
        <p className={styles.warn}>
          Provisional coverage only. {assignmentReadiness === 'setup'
            ? 'Assignment setup is required.'
            : 'Current authoritative assignment evidence is unavailable or pending.'}
          {' '}Accountable assignment counts are unknown; the coverage bars do not establish ownership.
        </p>
      )}

      {/* ⚠️ PARTIAL IS DESIGNED, NOT DEFAULTED. The bars are computed from the
          board PLUS the trigger list; if the trigger read failed, coverage is
          unknown for everything and the chart would silently become one giant
          "not readable" bar that looks like a finding. It says so instead. */}
      {coverageStatus === 'error' && (
        <p className={styles.warn}>
          Partial: the board read fine but the trigger list did not, and the trigger list is where
          this chart learns which rep covers which conference. Every team below is counted as not
          readable for that reason — this is not a measurement that nobody is assigned to them.
        </p>
      )}

      {/* ⚠️ EVERY BAR IS DECLARED A READOUT, AND check-clickable WAS RIGHT TO
          ASK. It reads a row rendered from a record and refuses it unless it
          either does something or says it does not — and the first version of
          this chart was flagged INERT at exactly this line. A bar IS a
          readout: the destination a rep would want from it is "show me
          Philip's 34 teams", which is a filter this board does not have, and
          wiring a row to a view that does not exist is how a dead control
          ships. So it is declared, in the same shape the team tables on this
          page already use, rather than given an invented href. */}
      <ul className={styles.bars}>
        {v.bars.map((b) => (
          <li key={b.rep} className={styles.row} aria-disabled="true">
            <span className={styles.name} title={b.display}>{b.display}</span>
            <span className={styles.track}>
              <span className={styles.fill} style={{ width: pct(b.n) }} />
            </span>
            <b className={styles.n}>{b.n.toLocaleString()}</b>
          </li>
        ))}

        <li className={`${styles.row} ${styles.holeRow}`} aria-disabled="true">
          <span className={styles.name}>Needs a desk pick<span className={styles.sub}>conference candidates without a confirmed school decision</span></span>
          <span className={styles.track}><span className={`${styles.fill} ${styles.fillQuiet}`} style={{ width: pct(v.needsPick) }} /></span>
          <b className={styles.n}>{v.needsPick.toLocaleString()}</b>
        </li>

        {/* 🔴 THE TWO HOLES. Always rendered, even at zero — a chart that drops
            its unassigned bar when it reaches zero cannot be read as "we closed
            it", because an absent bar and a zero bar look the same. */}
        <li className={`${styles.row} ${styles.holeRow}`} aria-disabled="true">
          <span className={styles.name}>
            No desk
            <span className={styles.sub}>nobody covers the body</span>
          </span>
          <span className={styles.track}>
            <span className={`${styles.fill} ${styles.fillBad}`} style={{ width: pct(v.noDesk) }} />
          </span>
          <b className={styles.n}>{v.noDesk.toLocaleString()}</b>
        </li>
        <li className={`${styles.row} ${styles.holeRow}`} aria-disabled="true">
          <span className={styles.name}>
            Not readable here
            <span className={styles.sub}>no assigned-rep field on a team record</span>
          </span>
          <span className={styles.track}>
            <span className={`${styles.fill} ${styles.fillQuiet}`} style={{ width: pct(v.unresolvable) }} />
          </span>
          <b className={styles.n}>{v.unresolvable.toLocaleString()}</b>
        </li>
      </ul>

      {/* 🔴 THE RECONCILIATION, ON THE FACE. Every team lands in exactly one of
          the three, and the sum is the membership — shown, not asserted, for the
          same reason the board above shows its own. */}
      <p className={v.reconciles ? styles.muted : styles.bad}>
        {v.reconciles
          ? `${v.reconcileLine}.`
          : 'These do not sum to all teams on the board. Do not use this chart.'}
      </p>

      {v.noDeskTeams.length > 0 && (
        <p className={styles.bad}>No desk: {v.noDeskTeams.join(', ')}.</p>
      )}

      <details className={styles.more}>
        <summary>How this is counted, and what it cannot see</summary>
        <p className={styles.muted}>{CHART_NOTE}</p>
        {v.manyToOne && (
          <p className={styles.muted}>
            The rep bars above sum to {barSum.toLocaleString()} across {(v.owned + v.needsPick).toLocaleString()}{' '}
            teams with a named desk or split coverage, because a conference shared by two or three reps puts each of
            its teams onto each of their assignments.
          </p>
        )}
        {v.paired && (
          <p className={styles.muted}>
            Standing support is named on the primary rep&apos;s bar: Philip with Madison, Zach with Alexis.
            This is one desk, not a second assignment. Independently assigned work remains on its own rep&apos;s bar.
          </p>
        )}
        <p className={styles.muted}>{UNRESOLVABLE_NOTE}</p>
        <p className={styles.muted}>{CHART_SOURCE_NOTE}</p>
      </details>
    </section>
  )
}
